import { ClientStatistics } from "../models/statistics.models";
import axios from "axios";
import { auth } from "../config/firebase";
import { FetchedFile, FileWithId } from "../models/uploaded-file";
import { User } from "../store/user.store";
import { axiosService } from "./axios.service";
import { ResultFile } from "../models/user.models";

export interface CorrespondenceResponse {
  product: {
    id: string;
    userId: string;
    crmProfileId: string;
    fullName: string;
    email: string;
    transactionId: string;
    isAnalyzed: boolean;
    error: string;
    createdAt: string;
    updatedAt: string;
    hasPaid: boolean;
    pdfUrl: string;
    productRequestId: string;
    isInProcess?: boolean;
    delayedTo?: string;
    force?: "analyze" | "not-analyze";
    formData: { correspondence: Record<string, string | number> };
  };
  productCrm?: Product;
  createdBy: User;
  attachments: FetchedFile[];
}

export interface WebIntelligenceResponse {
  product: {
    id: string;
    userId: string;
    crmProfileId: string;
    fullName: string;
    email: string;
    country: string;
    transactionId: string;
    isAnalyzed: boolean;
    error: string;
    createdAt: string;
    updatedAt: string;
    hasPaid: boolean;
    pdfUrl: string;
    productRequestId: string;
    isInProcess?: boolean;
    delayedTo?: string;
    force?: "analyze" | "not-analyze";
    formData: Record<string, string | number>;
  };
  createdBy: User;
  productCrm?: Product;
  attachments: FetchedFile[];
}

export interface BlockchainAnalysisResponse {
  product: {
    id: string;
    userId: string;
    fullName: string;
    email: string;
    hasPaid: boolean;
    isAnalysed: boolean;
    createdAt: number;
    pdfGenerated?: boolean;
    isInProcess?: boolean;
    delayedTo?: string;
    force?: "analyze" | "not-analyze";
    analysisError?: string;
    productRequestId: string;
    analysisResults?: {
      riskScore: number;
      riskLevel: string;
      analysis: string;
      recommendations: string;
      transactionDetails?: {
        from: string;
        to: string;
        amount: number;
        timestamp: number;
        entity: string;
      };
    };
    formData: Record<string, string | number>;
  };
  productCrm?: Product;
  createdBy: User;
  attachments: FetchedFile[];
}

export interface TrackMasterResponse {
  product: {
    link: string;
    id: string;
    Name: string;
    status: string;
    contractSigned: boolean;
    totalAmount: number;
    Created_Time: string;
    deals: Deal[];
    productRequestType: string;
    resultFile: null | ResultFile[];
    requestDetails: Record<string, string>;
    leftToPay: number | null;
    paid: number;
    data: Array<{
      id: number;
      userId: string;
      productRequestId: string | null;
      ip: string;
      ipDetails: {
        response: string;
        country_code: string;
        country_name: string;
        region_name: string;
        city_name: string;
        latitude: number;
        longitude: number;
        zip_code: string;
        time_zone: string;
        isp: string;
        domain: string;
        net_speed: string;
        idd_code: string;
        area_code: string;
        weather_station_code: string;
        weather_station_name: string;
        mcc: string;
        mnc: string;
        mobile_brand: string;
        elevation: number;
        usage_type: string;
        address_type: string;
        category: string;
        category_name: string;
        credits_consumed: number;
      };
      host: string;
      source: string;
      domain: string;
      brand: string;
      time: number;
      country: string;
      isp: string;
      vpn: string;
      new: number;
      archive: number;
      photo_url: string;
    }>;
  };
  createdBy: User | null;
  productCrm?: Product;

  attachments: FetchedFile[];
}

export type ProductRequestType =
  | "Web Intelligence"
  | "Blockchain Analysis"
  | "Correspondence"
  | "Track Master";
export type ClientFullProductData =
  | ({ type: "Web Intelligence" } & WebIntelligenceResponse)
  | ({ type: "Blockchain Analysis" } & BlockchainAnalysisResponse)
  | ({ type: "Correspondence" } & CorrespondenceResponse)
  | ({ type: "Track Master" } & TrackMasterResponse);
interface Deal {
  id: string;
  amount: number;
  createdTime: string;
  chargedAmount?: number | null;
  invoice?: string | null;
}

type RequestDetails = Record<string, any>;

export interface Product {
  id: string;
  name: string;
  type: ProductRequestType;
  status: string;
  totalAmount?: number | null;
  leftToPay?: number | null;
  paid: number;
  payed: boolean;
  contractSigned: boolean;
  createdTime: string;
  requestDetails: RequestDetails;
  deals: Deal[];
}

interface ClientInfo {
  crmProfileId: string;
  id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: string;
  riskLevel: string;
  clientStatus: string;
  createdTime: string;
  lastActivityTime: string;
  platformId: string;
}

interface Owner {
  id: string;
  name: string;
  email: string;
}

export interface ClientFullDetailsResponse {
  client: ClientInfo;
  owner: Owner;
  products: Product[];
  totalProducts: number;
  totalPaidAmount: number;
}

interface AgentClient {
  First_Name: string;
  Last_Name: string;
  Full_Name: string;
  Email: string;
  Description: string;
  Phone: string;
  Created_Time: string;
  Lead_Source: string;
  Country: string;
  id: string;
}

export interface AgentClientsResponse {
  data: AgentClient[];
  info: {
    count: number;
    page: number;
    per_page: number;
    more_records: boolean;
  };
}

export interface AdminClient {
  email: string;
  crmProfileId: string;
  id: string;
  name: string;
  phoneNumber: string;
}

export interface GetAgentClientsParams {
  page: number;
  perPage: number;
  query?: string;
}

export interface AdminActionResponse {
  success: boolean;
  message: string;
}

export interface CreateAgentRequest {
  email: string;
  password?: string;
}

export interface CreateAgentResponse {
  success: boolean;
  message: string;
  agentId?: string;
}

export interface CreateSystemUserRequest {
  email: string;
  fullName: string;
  isClient: boolean;
  password?: string;
}

export interface CreateSystemUserResponse {
  success: boolean;
  message: string;
  userId?: string;
}

export interface SystemUser {
  id: string;
  email: string;
  fullName: string;
  isClient: boolean;
  createdAt: string;
  crmProfileId?: string;
}

export interface GetSystemUserResponse {
  success: boolean;
  message: string;
  user?: SystemUser;
}

export interface UpdateSystemUserRequest {
  email?: string;
  fullName?: string;
  isClient?: boolean;
}

export interface UpdateSystemUserResponse {
  success: boolean;
  message: string;
  user?: SystemUser;
}

export interface GetProductFullDataPayload {
  clientCrmProfileId: string;
  productId: string;
  productRequestType: ProductRequestType;
}

export interface SetProductRequestFlags {
  userCrmProfileId: string;
  productRequestId: string;
  productRequestType: ProductRequestType;
  flags: {
    delayedTo: string | undefined | null;
    force: "analyze" | "not-analyze";
  };
}
export interface ProcessProductRequestImmediate {
  userCrmProfileId: string;
  productRequestId: string;
  productRequestType: ProductRequestType;
  sendMailToUser?: boolean;
}

export interface GetAllClientFilesPayload {
  clientCrmProfileId: string;
  page: number;
  limit: number;
}

const BASE_URL = import.meta.env.VITE_BACKEND_URL;

export class AgentService {
  static async getAgentClients(
    params: GetAgentClientsParams
  ): Promise<AgentClientsResponse> {
    try {
      const { page, perPage, query = "" } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        perPage: perPage.toString(),
        ...(query && { query: query }),
      });

      return await axiosService.get<AgentClientsResponse>(
        `/agent-actions/clients?${queryParams.toString()}`
      );
    } catch (error) {
      console.error("Error fetching agent clients:", error);
      throw error;
    }
  }

  static async getClientsDealsStatistics(
    periodInDays?: number
  ): Promise<ClientStatistics> {
    try {
      return axiosService.get(
        `agent-actions/statistics?periodInDays=${periodInDays}`
      );
    } catch (error) {
      console.error("Error fetching client׳s statistics", error);

      throw error;
    }
  }

  static async getClientDetails(
    clientId: string
  ): Promise<ClientFullDetailsResponse> {
    try {
      return await axiosService.get<ClientFullDetailsResponse>(
        `${BASE_URL}/agent-actions/client/${clientId}`
      );
    } catch (error) {
      console.error("Error fetching client details:", error);
      throw error;
    }
  }

  static async resendPasswordReset(
    email: string
  ): Promise<{ message: string; sent: boolean }> {
    try {
      return await axiosService.post<{ message: string; sent: boolean }>(
        `${BASE_URL}/agent-actions/resend-reset-password-email`,
        { email }
      );
    } catch (error) {
      console.error("Error sending password reset:", error);
      throw error;
    }
  }

  static async setClientPassword(
    email: string,
    newPassword: string
  ): Promise<{ message: string; success: boolean }> {
    try {
      return await axiosService.post<{ message: string; success: boolean }>(
        `${BASE_URL}/agent-actions/set-client-password`,
        { email, newPassword }
      );
    } catch (error) {
      console.error("Error setting client password:", error);
      throw error;
    }
  }

  static async sendEmailToClient(
    clientId: string,
    subject?: string,
    message?: string
  ): Promise<AdminActionResponse> {
    try {
      return await axiosService.post<AdminActionResponse>(
        `${BASE_URL}/agent-actions/clients/${clientId}/send-email`,
        { subject, message }
      );
    } catch (error) {
      console.error("Error sending email to client:", error);
      throw error;
    }
  }

  static async deactivateClient(
    clientId: string
  ): Promise<AdminActionResponse> {
    try {
      return await axiosService.patch<AdminActionResponse>(
        `${BASE_URL}/agent-actions/clients/${clientId}/deactivate`,
        {}
      );
    } catch (error) {
      console.error("Error deactivating client:", error);
      throw error;
    }
  }

  /**
   * Reactivate a client account
   * @param clientId - Firebase ID of the client
   * @returns Promise<AdminActionResponse>
   */
  static async reactivateClient(
    clientId: string
  ): Promise<AdminActionResponse> {
    try {
      return await axiosService.patch<AdminActionResponse>(
        `${BASE_URL}/agent-actions/clients/${clientId}/reactivate`,
        {}
      );
    } catch (error) {
      console.error("Error reactivating client:", error);
      throw error;
    }
  }

  /**
   * Update client information
   * @param clientId - Firebase ID of the client
   * @param updates - Partial client data to update
   * @returns Promise<AdminClient>
   */
  static async updateClient(
    clientId: string,
    updates: Partial<Omit<AdminClient, "id">>
  ): Promise<AdminClient> {
    try {
      return await axiosService.patch<AdminClient>(
        `${BASE_URL}/agent-actions/clients/${clientId}`,
        updates
      );
    } catch (error) {
      console.error("Error updating client:", error);
      throw error;
    }
  }

  /**
   * Create a new agent user (Admin only)
   * @param agentData - Email and optional password for the new agent
   * @returns Promise<CreateAgentResponse>
   */
  static async createAgent(
    agentData: CreateAgentRequest
  ): Promise<CreateAgentResponse> {
    try {
      return await axiosService.post<CreateAgentResponse>(
        `${BASE_URL}/agent-actions/register-agent`,
        agentData
      );
    } catch (error) {
      console.error("Error creating agent:", error);
      throw error;
    }
  }

  /**
   * Create a new system user (Admin only)
   * @param userData - Email, fullName, and isClient flag for the new user
   * @returns Promise<CreateSystemUserResponse>
   */
  static async createSystemUser(
    userData: CreateSystemUserRequest
  ): Promise<CreateSystemUserResponse> {
    try {
      return await axiosService.post<CreateSystemUserResponse>(
        `${BASE_URL}/agent-actions/create-user`,
        userData
      );
    } catch (error) {
      console.error("Error creating system user:", error);
      throw error;
    }
  }

  /**
   * Get system user by email (Admin only)
   * @param email - Email of the user to fetch
   * @returns Promise<GetSystemUserResponse>
   */
  static async getSystemUserByEmail(
    email: string
  ): Promise<GetSystemUserResponse> {
    try {
      return await axiosService.get<GetSystemUserResponse>(
        `${BASE_URL}/agent-actions/user/${encodeURIComponent(email)}`
      );
    } catch (error) {
      console.error("Error fetching system user:", error);
      throw error;
    }
  }

  /**
   * @param updates - Updates to apply to the user
   * @returns Promise<UpdateSystemUserResponse>
   */
  static async updateSystemUser(
    updates: UpdateSystemUserRequest
  ): Promise<UpdateSystemUserResponse> {
    try {
      return await axiosService.put<UpdateSystemUserResponse>(
        `${BASE_URL}/agent-actions/client-info`,
        updates
      );
    } catch (error) {
      console.error("Error updating system user:", error);
      throw error;
    }
  }

  static async updateClientKYCStatus(
    clientId: string,
    kycStatus: "Yes" | "No"
  ): Promise<{ message: string; success: boolean }> {
    try {
      return await axiosService.post<{ message: string; success: boolean }>(
        `${BASE_URL}/agent-actions/update-kyc-status`,
        {
          clientId,
          KYC_Status: kycStatus,
        }
      );
    } catch (error) {
      console.error("Error updating KYC status:", error);
      throw error;
    }
  }

  static getProductFullData = async (
    payload: GetProductFullDataPayload
  ): Promise<ClientFullProductData> => {
    const { clientCrmProfileId, productId, productRequestType } = payload;
    try {
      return await axiosService.get<ClientFullProductData>(
        `${BASE_URL}/agent-actions/client/${clientCrmProfileId}/${productId}/${productRequestType}`
      );
    } catch (error) {
      console.error("Error fetching product details:", error);
      throw error;
    }
  };
  static setProductRequestFlags = async (
    payload: SetProductRequestFlags
  ): Promise<ClientFullProductData> => {
    const { userCrmProfileId, productRequestId, productRequestType } = payload;
    try {
      return await axiosService.put<ClientFullProductData>(
        `${BASE_URL}/agent-actions/update-product-request-flags`,
        {
          userCrmProfileId,
          productRequestId,
          productRequestType,
          flags: payload.flags,
        }
      );
    } catch (error) {
      console.error("Error fetching product details:", error);
      throw error;
    }
  };
  static processProductRequestImmediate = async (payload: ProcessProductRequestImmediate): Promise<ClientFullProductData> => {
    const { userCrmProfileId, productRequestId, productRequestType, sendMailToUser } = payload;
    try {
      return await axiosService.post<ClientFullProductData>(`${BASE_URL}/agent-actions/process-product-request-immediate`, {
        userCrmProfileId,
        productRequestId,
        productRequestType,
        sendMailToUser,
      });
    } catch (error) {
      console.error("Error fetching product details:", error);
      throw error;
    }
  };

  static getAllClientFiles = async (
    payload: GetAllClientFilesPayload
  ): Promise<{ list: FetchedFile[]; total: number }> => {
    const { clientCrmProfileId, limit, page } = payload;
    try {
      return await axiosService.get<{ list: FetchedFile[]; total: number }>(
        `${BASE_URL}/agent-actions/client/files/${clientCrmProfileId}?page=${page}&limit=${limit}`
      );
    } catch (error) {
      console.error("Error fetching product details:", error);
      throw error;
    }
  };

  static async handleCrmFileAction(
    file: FileWithId,
    action: "download" | "preview",
    crmProfileId: string
  ) {
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) return;
      const response = await axios.get(
        `${BASE_URL}/agent-actions/downloadattachment/${crmProfileId}/${file.id}`,
        {
          responseType: "blob",
          headers: {
            Authorization: token,
          },
        }
      );

      const url = window.URL.createObjectURL(response.data);

      if (action === "preview") {
        window.open(url, "_blank");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = file.content?.name || "downloaded-file";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error handling file action:", error);
    }
  }
}
